/**
 * Data Sync Helpers
 *
 * Dunne coördinatie-laag tussen ApiService en useDataStore. Elke helper
 * roept eerst de API aan en update pas na succesvolle resolve de store
 * (pessimistisch patroon). Bij fout blijft de cache consistent met server.
 *
 * Gebruik deze helpers in plaats van directe ApiService-calls overal waar
 * de wijziging direct zichtbaar moet zijn in andere consumers (DetailPanel,
 * PlaceManager, etc.). De acceptance-garantie uit het refactor-plan hangt
 * hiervan af.
 */
import { ApiService } from './ApiService';
import { isLocalEventId } from './EventUtils';
import { useDataStore } from '../stores/useDataStore';
import type { Person, GenealogyEvent, Source, Relationship } from '../types';
import type { RelatieItem } from './api/relationships';
import { ApiError, personToServer, type ZelfMelden } from './api/base';
import { bestaandePersoon } from '../utils/foutUitleg';
import { stabiel } from '../utils/driewegVergelijking';

/** Nieuwe relatierijen in de store, zonder dubbele (op id). */
export function voegRelatiesToe(nieuw: Relationship[]) {
  if (!nieuw.length) return;
  const { relationships, setRelationships } = useDataStore.getState();
  const ids = new Set(relationships.map((r) => r.id));
  setRelationships([...relationships, ...nieuw.filter((r) => !ids.has(r.id))]);
}

export async function savePersonWithCache(
  patch: Partial<Person>,
  opts?: { relationships?: RelatieItem[]; clientId?: string } & ZelfMelden
): Promise<
  Person & {
    warnings?: Array<{ field: string; message: string; code: string }>;
    relationships?: Relationship[];
  }
> {
  let saved: Awaited<ReturnType<typeof slaPersoonOp>>;
  try {
    saved = await slaPersoonOp(patch, opts);
  } catch (err) {
    // #1825 — de persoon bestaat wel (eerste poging), alleen de latere aanpassing niet.
    // De relaties die de eerste poging legde horen er dan ook bij (familie en boom tonen ze).
    const bestaand = bestaandePersoon(err);
    if (bestaand) {
      useDataStore.getState().applyPersonSnapshot(alsPersoon(bestaand));
      voegRelatiesToe(bestaand.relationships ?? []);
    }
    throw err;
  }
  // #800 / #835 — de server geeft de volledige persoon terug (lijstvorm, mét
  // events en bronnen): die vervangt onze kopie en ververst zijn events in de
  // store. Waarschuwingen horen bij dit antwoord, niet bij de persoon.
  useDataStore.getState().applyPersonSnapshot(alsPersoon(saved));
  return saved;
}

/** Het antwoord van savePerson zonder wat bij het antwoord hoort (niet bij de persoon). */
function alsPersoon(saved: Awaited<ReturnType<typeof ApiService.savePerson>>): Person {
  const { warnings: _w, relationships: _r, bestond: _b, ...persoon } = saved;
  return persoon as Person;
}

const metBestaande = (err: unknown, persoon: Person) => {
  if (err && typeof err === 'object')
    (err as { bestaandePersoon?: Person }).bestaandePersoon = persoon;
  return err;
};

// Geen invoer van de gebruiker: id/versie, apart opgeslagen events, en wat de server
// zelf afleidt (gemeente/provincie/land uit de plaats, Sosa).
const VOLATIEL = new Set(['id', 'projectId', 'updatedAt', 'createdAt', 'events', 'sosaNumber']);
const afgeleid = (k: string) => VOLATIEL.has(k) || /(Municipality|State|Country)$/.test(k);

/**
 * #1825 — staan op de server precies de velden die deze opslag stuurde? Alleen de
 * verstuurde velden tellen; `null` en '' zijn gelijk. Twijfel = verschil: dan meldt
 * de editor het gewone versieconflict.
 */
export function zelfdeVelden(verstuurd: Partial<Person>, server: Partial<Person>): boolean {
  return Object.keys(afwijkendeVelden(verstuurd, server)).length === 0;
}

const isObject = (w: unknown): w is Record<string, unknown> =>
  !!w && typeof w === 'object' && !Array.isArray(w);
/** Niets (null, '', false, [], {}) is gelijk; een lijst van waarden is ongeordend. */
const leeg = (w: unknown): unknown =>
  w === null ||
  w === undefined ||
  w === false ||
  w === '' ||
  (Array.isArray(w) && !w.length) ||
  (isObject(w) && !Object.keys(w).length)
    ? ''
    : Array.isArray(w) && w.every((x) => !x || typeof x !== 'object')
      ? [...w].sort()
      : w;
/** Wat wij stuurden staat er zo: extra sleutels die de server aanvult tellen niet. */
function gelijk(mijn: unknown, hun: unknown): boolean {
  const a = leeg(mijn);
  const b = leeg(hun);
  if (isObject(a) && isObject(b)) return Object.keys(a).every((k) => gelijk(a[k], b[k]));
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((x, i) => gelijk(x, b[i]));
  return stabiel(a) === stabiel(b);
}

/**
 * De verstuurde velden die op de server anders staan. Vergeleken in de vorm die de server
 * krijgt (`personToServer`): een veld dat de server niet kent telt niet, en 'O' en 'U' zijn
 * allebei "unknown". De levensstatus zet de DB-trigger (migratie 193), tenzij handmatig.
 */
function afwijkendeVelden(verstuurd: Partial<Person>, server: Partial<Person>): Partial<Person> {
  return Object.fromEntries(
    Object.entries(verstuurd).filter(([k, w]) => {
      // undefined gaat niet mee in de JSON-body: niet verstuurd.
      if (w === undefined || afgeleid(k)) return false;
      if (k === 'isLiving' && verstuurd.isLivingHandmatig !== true) return false;
      const mijn = personToServer({ [k]: w });
      const hun = personToServer({ [k]: (server as Record<string, unknown>)[k] });
      return Object.keys(mijn).some((s) => !gelijk(mijn[s], hun[s]));
    })
  ) as Partial<Person>;
}

/** Niemand wijzigde de persoon na de POST (die in één transactie liep: zelfde tijdstip). */
const onaangeraakt = (p: Partial<Person>) => {
  const ms = (x?: string) => (x ? new Date(x).getTime() : NaN);
  return ms(p.createdAt) === ms(p.updatedAt);
};

/** Personen waarvan de laatste PUT in een time-out liep (misschien toch opgeslagen). */
const naTimeout = new Set<string>();

/**
 * #1825 — PUT/POST via ApiService. Liep de vorige PUT van deze persoon in een
 * time-out en geeft deze een versieconflict, dan kan dat onze eigen, wél gelukte
 * opslag zijn: staan op de server precies onze velden, dan is het opgeslagen.
 */
export async function slaPersoonOp(
  patch: Partial<Person>,
  opts?: { relationships?: RelatieItem[]; clientId?: string } & ZelfMelden
): ReturnType<typeof ApiService.savePerson> {
  const id = patch.id;
  try {
    const saved = await ApiService.savePerson(patch, opts);
    if (id) naTimeout.delete(id);
    // #1825 — herhaalde POST na een time-out: de server gaf de persoon van de eerste poging
    // terug en negeerde deze body. Wat de gebruiker sindsdien aanpaste, gaat met een PUT
    // alsnog mee (verse versie uit het antwoord). Relaties niet: die legde de eerste poging
    // al, of de aanroeper vult ze zelf aan (addParentsOpslaan) — anders ontstaan dubbele.
    // Wijzigde iemand anders hem intussen, dan niet overschrijven: het gewone versieconflict.
    // Faalt de PUT, dan bestaat de persoon tóch: de fout draagt hem (bestaandePersoon).
    if (!id && saved.bestond) {
      const anders = afwijkendeVelden(patch, saved);
      if (Object.keys(anders).length) {
        if (!onaangeraakt(saved))
          throw metBestaande(
            new ApiError('This person was modified by another user', 409, {
              code: 'DUPLICATE_ENTRY',
              details: { conflict: true },
            }),
            saved
          );
        try {
          const bijgewerkt = await ApiService.savePerson(
            { ...anders, id: saved.id, projectId: saved.projectId, updatedAt: saved.updatedAt },
            opts?.zelfMelden ? { zelfMelden: true } : undefined
          );
          return { ...bijgewerkt, relationships: saved.relationships };
        } catch (err) {
          throw metBestaande(err, saved);
        }
      }
    }
    return saved;
  } catch (err) {
    const e = err as { code?: string; status?: number };
    if (id && e?.code === 'REQUEST_TIMEOUT') naTimeout.add(id);
    else if (id && e?.status === 409 && naTimeout.has(id)) {
      const server = await ApiService.getPerson(id).catch((fout) => {
        console.warn('[savePersonWithCache] Persoon ophalen na time-out + 409 mislukt:', {
          personId: id,
          fout,
        });
        return null;
      });
      if (server) {
        naTimeout.delete(id);
        // Zelfde vorm als het PUT-antwoord, zonder waarschuwingen of relaties.
        if (zelfdeVelden(patch, server)) return server;
      }
    }
    throw err;
  }
}

export async function deletePersonWithCache(personId: string): Promise<void> {
  await ApiService.deletePerson(personId);
  useDataStore.getState().removePerson(personId);
}

export async function saveEventWithCache(
  patch: Partial<GenealogyEvent>,
  options?: { sourceIds?: string[]; clientId?: string; expectedVersion?: string } & ZelfMelden
): Promise<GenealogyEvent> {
  const saved = await ApiService.saveEvent(patch, options);
  const store = useDataStore.getState();
  if (patch.id) {
    store.updateEvent(saved.id, saved);
  } else {
    store.addEvent(saved);
  }
  return saved;
}

export async function deleteEventWithCache(eventId: string, opts?: ZelfMelden): Promise<void> {
  // #1647 — een tijdelijk (lokaal) id is nooit op de server geweest; de DELETE
  // gaf een 500 op het uuid-type en de rij bleef onverwijderbaar staan.
  if (!isLocalEventId(eventId)) await ApiService.deleteEvent(eventId, opts);
  useDataStore.getState().removeEvent(eventId);
}

export async function saveSourceWithCache(patch: Partial<Source>): Promise<Source> {
  const saved = await ApiService.saveSource(patch);
  const store = useDataStore.getState();
  if (patch.id) {
    store.updateSource(saved.id, saved);
  } else {
    store.addSource(saved);
  }
  return saved;
}

export async function deleteSourceWithCache(sourceId: string): Promise<void> {
  await ApiService.deleteSource(sourceId);
  useDataStore.getState().removeSource(sourceId);
}
